import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { getIdentity, Identity } from '../api/identity';
import { useApiAuth, useAxiosSwagger } from '../hooks/useAxiosSwagger';
import { describeError } from '../api/requeue';
import { useAuth } from './useAuth';

/** Per account: whether it was an admin last time, so the header need not wait for the answer. */
const ADMIN_KEY_PREFIX = 'identity:admin:';

const rememberedAdmin = (account?: string): boolean => {
    try {
        return !!account && window.localStorage.getItem(ADMIN_KEY_PREFIX + account) === '1';
    } catch {
        return false;
    }
};

const rememberAdmin = (account: string | undefined, isAdmin: boolean) => {
    try {
        if (account) {
            window.localStorage.setItem(ADMIN_KEY_PREFIX + account, isAdmin ? '1' : '0');
        }
    } catch {
        // Storage that is off or full only costs the head start.
    }
};

/** Where the answer to "who am I" stands. */
export type IdentityStatus = 'loading' | 'ready' | 'error';

export interface IdentityState {
    /** What the API said, or null while there is no answer. */
    identity: Identity | null;
    status: IdentityStatus;
    /** What went wrong; null while nothing did. */
    error: string | null;
    /**
     * Whether this account sees more than the error log.
     *
     * False while the answer is still on its way, and false when it never came. Both are
     * deliberate: a screen that assumes admin until told otherwise draws six panels and
     * then snatches them away, and a screen that assumes admin when the call failed shows
     * buttons the API will refuse anyway.
     */
    isAdmin: boolean;
    /**
     * Whether this account is probably an admin: the answer itself once it is in, and while
     * it is on its way the answer of last time in this browser. Only for what may show a
     * moment too early - a link in the header - never for what the API would refuse.
     */
    likelyAdmin: boolean;
    /** Ask again, for a failure that was only a hiccup. */
    reload: () => void;
}

const IdentityContext = createContext<IdentityState>({
    identity: null,
    status: 'loading',
    error: null,
    isAdmin: false,
    likelyAdmin: false,
    reload: () => undefined,
});

/**
 * Fetches the caller's roles once and hands them to the whole app.
 *
 * A provider rather than a hook per screen, because the answer is the same everywhere and
 * changes only at sign-in. Belongs inside AxiosProvider: it needs the axios instance, and
 * it has to wait for the token that instance is built around.
 */
export const IdentityProvider: React.FunctionComponent<{
    children: ReactNode;
}> = (props) => {
    const axios = useAxiosSwagger();
    const { loading: tokenLoading, failure } = useApiAuth();
    const { user } = useAuth();
    const account = user?.sub;

    const [identity, setIdentity] = useState<Identity | null>(null);
    const [status, setStatus] = useState<IdentityStatus>('loading');
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState<number>(0);

    useEffect(() => {
        if (!axios || tokenLoading) {
            return;
        }

        /*
            No token, no question worth asking: the call would come back 401 and land as a
            role failure on top of the token banner that is already on screen. The banner
            says what to do about it, and a successful retry rebuilds the axios instance,
            which runs this effect again.
        */
        if (failure) {
            setStatus('error');
            setError(failure.message);

            return;
        }

        let cancelled = false;

        setStatus('loading');

        (async () => {
            try {
                const next = await getIdentity(axios);

                if (!cancelled) {
                    rememberAdmin(account, next.isAdmin);
                    setIdentity(next);
                    setError(null);
                    setStatus('ready');
                }
            } catch (ex) {
                console.error('Could not read the roles of this account', ex);

                if (!cancelled) {
                    setError(describeError(ex));
                    setStatus('error');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [axios, tokenLoading, failure, attempt, account]);

    const reload = useCallback(() => setAttempt((round) => round + 1), []);

    const value: IdentityState = useMemo(() => {
        const isAdmin = status === 'ready' && identity?.isAdmin === true;

        return {
            identity,
            status,
            error,
            isAdmin,
            likelyAdmin: status === 'loading' ? rememberedAdmin(account) : isAdmin,
            reload,
        };
    }, [identity, status, error, reload, account]);

    return (
        <IdentityContext.Provider value={value}>
            {props.children}
        </IdentityContext.Provider>
    );
};

/** The roles of the signed-in account, and what they add up to. */
export const useIdentity = () => useContext(IdentityContext);
